import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  CheckCircleIcon,
  PrinterIcon,
  ReceiptRefundIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const today = new Date().toISOString().split('T')[0];

const ClientLedger = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAccess, hasRole } = useAuth();
  const [client, setClient] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('all');

  const canManageClients = hasRole(['admin', 'accounts']);

  const {
    register: registerDebit,
    handleSubmit: handleDebitSubmit,
    reset: resetDebit,
    formState: { errors: debitErrors, isSubmitting: isSavingDebit }
  } = useForm({
    defaultValues: { amount: '', description: '', date: today, referenceNumber: '' }
  });

  const {
    register: registerCredit,
    handleSubmit: handleCreditSubmit,
    reset: resetCredit,
    formState: { errors: creditErrors, isSubmitting: isSavingCredit }
  } = useForm({
    defaultValues: { amount: '', date: today, checkNumber: '', dueDate: '' }
  });

  const formatDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleDateString();
  };

  const actorLabel = (person) => {
    if (!person?.name) return 'N/A';
    return person.employeeId ? `${person.name} (${person.employeeId})` : person.name;
  };

  const csvEscape = (value) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };

  const cleanPayload = (data) => {
    const payload = {};
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) payload[key] = trimmed;
      } else if (value !== undefined && value !== null && value !== '') {
        payload[key] = value;
      }
    });
    return payload;
  };

  const fetchClient = useCallback(async () => {
    if (!canAccess('clients')) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/clients/${id}`);
      setClient(response.data.client);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch client ledger');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  }, [canAccess, id, navigate]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const ledgerRows = useMemo(() => {
    const debitEntries = client?.debitEntries || [];
    const creditEntries = client?.creditEntries || [];

    const debits = debitEntries.map((entry) => ({
      id: entry._id,
      type: 'debit',
      category: 'Invoice / Debit',
      date: entry.date,
      createdAt: entry.createdAt,
      title: entry.description,
      reference: entry.referenceNumber || 'No reference',
      amount: entry.amount,
      signedAmount: entry.amount,
      status: 'billed',
      actorLabel: 'Billed by',
      actor: actorLabel(entry.billedBy)
    }));

    const credits = creditEntries.map((entry) => ({
      id: entry._id,
      type: 'credit',
      category: entry.status === 'pending' ? 'Pending Check' : entry.status === 'cleared' ? 'Cleared Credit' : 'Bounced Check',
      date: entry.date,
      createdAt: entry.createdAt,
      dueDate: entry.dueDate,
      title: entry.checkNumber || 'Incoming payment',
      reference: entry.checkNumber || 'No check number',
      amount: entry.amount,
      signedAmount: entry.status === 'cleared' ? -entry.amount : 0,
      status: entry.status,
      actorLabel: 'Recorded by',
      actor: actorLabel(entry.addedBy)
    }));

    let runningBalance = 0;
    return [...debits, ...credits]
      .sort((a, b) => new Date(a.date) - new Date(b.date) || new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
      .map((row) => {
        runningBalance += row.signedAmount;
        return { ...row, runningBalance };
      });
  }, [client]);

  const filteredRows = useMemo(() => {
    switch (activeView) {
      case 'debits':
        return ledgerRows.filter((row) => row.type === 'debit');
      case 'pending':
        return ledgerRows.filter((row) => row.status === 'pending');
      case 'cleared':
        return ledgerRows.filter((row) => row.status === 'cleared');
      case 'bounced':
        return ledgerRows.filter((row) => row.status === 'bounced');
      default:
        return ledgerRows;
    }
  }, [activeView, ledgerRows]);

  const onDebitSubmit = async (data) => {
    try {
      await api.post(`/api/clients/${id}/debit`, cleanPayload(data));
      toast.success('Invoice/debit entry added');
      resetDebit({ amount: '', description: '', date: today, referenceNumber: '' });
      fetchClient();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to add debit entry');
    }
  };

  const onCreditSubmit = async (data) => {
    try {
      await api.post(`/api/clients/${id}/credit`, cleanPayload(data));
      toast.success('Incoming check recorded');
      resetCredit({ amount: '', date: today, checkNumber: '', dueDate: '' });
      fetchClient();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to add credit entry');
    }
  };

  const updateCreditStatus = async (creditId, status) => {
    try {
      await api.put(`/api/clients/${id}/credit/${creditId}/status`, { status });
      toast.success(`Check marked ${status}`);
      fetchClient();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update check status');
    }
  };

  const exportCsv = () => {
    const rows = [
      ['Client', client.name],
      ['Contact', client.contactPerson || ''],
      ['Email', client.email || ''],
      ['Phone', client.phone || ''],
      ['Total Billed', Number(summary?.totalDebit || 0)],
      ['Cleared Credits', Number(summary?.totalCredit || 0)],
      ['Total Dues', Number(summary?.totalDues || 0)],
      [],
      ['Date', 'Category', 'Reference', 'Description', 'Responsible Person', 'Status', 'Debit', 'Credit', 'Running Balance'],
      ...filteredRows.map((row) => [
        formatDate(row.date),
        row.category,
        row.reference,
        row.title,
        row.actor,
        row.status,
        row.type === 'debit' ? Number(row.amount || 0) : '',
        row.type === 'credit' && row.status === 'cleared' ? Number(row.amount || 0) : '',
        Number(row.runningBalance || 0)
      ])
    ];

    const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${client.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-ledger-${activeView}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredRows.length} ledger rows`);
  };

  const printLedger = () => {
    window.print();
  };

  const statusClass = (status) => {
    switch (status) {
      case 'cleared':
        return 'bg-green-100 text-green-800';
      case 'bounced':
        return 'bg-red-100 text-red-800';
      case 'billed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (!canAccess('clients')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!client) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <Link to="/clients" className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 mb-3">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to clients
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{client.name}</h1>
          <p className="text-gray-600">Client account ledger and check reconciliation</p>
        </div>
        <div className="text-sm text-gray-600 md:text-right">
          <p>{client.contactPerson || 'No contact person'}</p>
          <p>{client.email || 'No email'} {client.phone ? `- ${client.phone}` : ''}</p>
          <p>Created by {actorLabel(client.createdBy)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <p className="text-sm font-medium text-gray-500">Total Billed</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(summary?.totalDebit)}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm font-medium text-gray-500">Cleared Credits</p>
          <p className="mt-1 text-2xl font-semibold text-green-600">{formatCurrency(summary?.totalCredit)}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm font-medium text-gray-500">Total Dues</p>
          <p className="mt-1 text-2xl font-semibold text-red-600">{formatCurrency(summary?.totalDues)}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm font-medium text-gray-500">Pending Checks</p>
          <p className="mt-1 text-2xl font-semibold text-yellow-600">
            {summary?.pendingChecks || 0} / {formatCurrency(summary?.pendingAmount)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-4 no-print">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap gap-2">
              {[
                ['all', 'All Activity'],
                ['debits', 'Invoices / Debits'],
                ['pending', 'Pending Checks'],
                ['cleared', 'Cleared Credits'],
                ['bounced', 'Bounced Checks']
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setActiveView(value)}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeView === value
                      ? 'bg-primary-100 text-primary-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button type="button" onClick={exportCsv} className="btn-secondary flex items-center justify-center">
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
                <button type="button" onClick={printLedger} className="btn-secondary flex items-center justify-center">
                  <PrinterIcon className="h-4 w-4 mr-2" />
                  Print
                </button>
              </div>
            </div>
          </div>

          <div className="print-only">
            <h2 className="text-xl font-semibold text-gray-900">{client.name} Ledger Statement</h2>
            <p className="text-sm text-gray-600">
              View: {activeView} | Generated {new Date().toLocaleString()}
            </p>
          </div>

          <div className="card ledger-print-section">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Categorized Ledger</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Debit</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credit</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider no-print">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-gray-500">No ledger entries in this category.</td>
                    </tr>
                  ) : (
                    filteredRows.map((row) => (
                      <tr key={`${row.type}-${row.id}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(row.date)}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{row.category}</div>
                          <div className="text-xs text-gray-500">{row.title}</div>
                          {row.dueDate && <div className="text-xs text-gray-500">Due {formatDate(row.dueDate)}</div>}
                          <div className="text-xs text-gray-500">{row.actorLabel} {row.actor}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{row.reference}</td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                          {row.type === 'debit' ? formatCurrency(row.amount) : '-'}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium text-green-700">
                          {row.type === 'credit' && row.status === 'cleared' ? formatCurrency(row.amount) : '-'}
                        </td>
                        <td className={`px-6 py-4 text-right text-sm font-semibold ${row.runningBalance > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                          {formatCurrency(row.runningBalance)}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusClass(row.status)}`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right no-print">
                          {canManageClients && row.type === 'credit' && row.status === 'pending' ? (
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => updateCreditStatus(row.id, 'cleared')}
                                className="btn-success text-xs px-2 py-1 flex items-center"
                              >
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Clear
                              </button>
                              <button
                                type="button"
                                onClick={() => updateCreditStatus(row.id, 'bounced')}
                                className="btn-danger text-xs px-2 py-1 flex items-center"
                              >
                                <XCircleIcon className="h-4 w-4 mr-1" />
                                Bounce
                              </button>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6 no-print">
          <div className="card p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Client Details</h2>
            <div className="text-sm text-gray-600 space-y-2">
              <p><span className="font-medium text-gray-500">Contact:</span> {client.contactPerson || 'N/A'}</p>
              <p><span className="font-medium text-gray-500">Email:</span> {client.email || 'N/A'}</p>
              <p><span className="font-medium text-gray-500">Phone:</span> {client.phone || 'N/A'}</p>
              <p><span className="font-medium text-gray-500">Address:</span> {client.address || 'N/A'}</p>
              {client.notes && <p><span className="font-medium text-gray-500">Notes:</span> {client.notes}</p>}
            </div>
          </div>

          {canManageClients && (
            <>
              <form onSubmit={handleDebitSubmit(onDebitSubmit)} className="card p-4 space-y-3">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center">
                  <ReceiptRefundIcon className="h-4 w-4 mr-2" />
                  Add Invoice / Debit
                </h2>
                <div>
                  <label className="label">Amount</label>
                  <input
                    {...registerDebit('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Amount must be positive' } })}
                    type="number"
                    step="0.01"
                    className="input-field"
                  />
                  {debitErrors.amount && <p className="mt-1 text-sm text-red-600">{debitErrors.amount.message}</p>}
                </div>
                <div>
                  <label className="label">Date</label>
                  <input {...registerDebit('date', { required: 'Date is required' })} type="date" className="input-field" />
                </div>
                <div>
                  <label className="label">Description</label>
                  <input {...registerDebit('description', { required: 'Description is required' })} className="input-field" />
                  {debitErrors.description && <p className="mt-1 text-sm text-red-600">{debitErrors.description.message}</p>}
                </div>
                <div>
                  <label className="label">Reference Number</label>
                  <input {...registerDebit('referenceNumber')} className="input-field" />
                </div>
                <button type="submit" disabled={isSavingDebit} className="btn-primary w-full">
                  {isSavingDebit ? 'Adding...' : 'Add Debit'}
                </button>
              </form>

              <form onSubmit={handleCreditSubmit(onCreditSubmit)} className="card p-4 space-y-3">
                <h2 className="text-sm font-semibold text-gray-900 flex items-center">
                  <BanknotesIcon className="h-4 w-4 mr-2" />
                  Add Incoming Check / Credit
                </h2>
                <div>
                  <label className="label">Amount</label>
                  <input
                    {...registerCredit('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Amount must be positive' } })}
                    type="number"
                    step="0.01"
                    className="input-field"
                  />
                  {creditErrors.amount && <p className="mt-1 text-sm text-red-600">{creditErrors.amount.message}</p>}
                </div>
                <div>
                  <label className="label">Received Date</label>
                  <input {...registerCredit('date', { required: 'Date is required' })} type="date" className="input-field" />
                </div>
                <div>
                  <label className="label">Check Number</label>
                  <input {...registerCredit('checkNumber')} className="input-field" />
                </div>
                <div>
                  <label className="label">Due Date</label>
                  <input {...registerCredit('dueDate')} type="date" className="input-field" />
                </div>
                <button type="submit" disabled={isSavingCredit} className="btn-primary w-full">
                  {isSavingCredit ? 'Recording...' : 'Record Credit'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientLedger;
